import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAtomValue } from "jotai";
import { sessionAtom } from "../../features/accounts/account.state";
import { OpenUpgradeModalFunc, UpgradeFuncAttrs } from "./upgrade-modal-types";
import { ExhaustionWall } from "./ExhaustionWall";
import { getExhaustionInfo } from "./useExhaustionCheck";
import { EntityType } from "../../features/data/data-types";

interface ExhaustionState {
  type: EntityType;
  title?: string;
  subtitle?: string;
  stayOnPage?: boolean;
  entryPoint?: string;
  current: number;
  limit: number;
  targetPlan?: string;
  returnUrl?: string;
  targetProducts?: number;
  onUpgradeClick?: () => void;
}

/**
 * The upgrade modal. Wired into `DialogContext`, so every `openUpgradeModal`
 * call in the app lands here — there is no second implementation.
 */
export const useExhaustionDialog = () => {
  const [state, setState] = useState<ExhaustionState | null>(null);
  const session = useAtomValue(sessionAtom);
  const navigate = useNavigate();

  const openUpgradeModal: OpenUpgradeModalFunc = (
    funcAttrs: UpgradeFuncAttrs,
  ) => {
    const attrs =
      typeof funcAttrs === "string" ? { type: funcAttrs } : funcAttrs;

    const type = (attrs.type ?? "products") as EntityType;
    const account = session?.account;

    // Get real usage data from account
    let current = 0;
    let limit = 0;

    if (account) {
      const info = getExhaustionInfo(account, type);
      if (info) {
        current = info.current;
        limit = info.limit;
      }
    }

    setState({
      type,
      title: attrs.title,
      subtitle: attrs.subtitle,
      stayOnPage: attrs.stayOnPage,
      entryPoint: attrs.entryPoint,
      current,
      limit,
      targetPlan: attrs.targetPlan,
      returnUrl: attrs.returnUrl,
      targetProducts: attrs.targetProducts,
      onUpgradeClick: attrs.onUpgradeClick,
    });

    if (type === "catalogs" && !attrs.stayOnPage) {
      navigate("/catalogs");
    }
  };

  const renderUpgradeModal = useCallback(() => {
    if (!state) return null;

    return (
      <ExhaustionWall
        type={state.type}
        current={state.current}
        limit={state.limit}
        mode="modal"
        isOpen={!!state}
        onClose={() => setState(null)}
        title={state.title}
        subtitle={state.subtitle}
        entryPoint={state.entryPoint}
        targetPlan={state.targetPlan}
        returnUrl={state.returnUrl}
        targetProducts={state.targetProducts}
        onUpgradeClick={state.onUpgradeClick}
      />
    );
  }, [state]);

  return { renderUpgradeModal, openUpgradeModal };
};
